import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, CheckCircle, Clock, Zap, RefreshCw } from 'lucide-react';
import { useContentQuality } from '@/hooks/useContentQuality';
import { useToast } from '@/hooks/use-toast';

export const ContentQualityDashboard: React.FC = () => {
  const { 
    report, 
    isAnalyzing, 
    lastAnalyzed, 
    errors, 
    canManageContent,
    analyzeContentQuality,
    fixAllIssues
  } = useContentQuality();
  
  const { toast } = useToast();

  useEffect(() => {
    if (canManageContent && !report) {
      analyzeContentQuality();
    }
  }, [canManageContent, report, analyzeContentQuality]);

  const handleAutoFix = async () => {
    try {
      const fixes = await fixAllIssues();
      toast({
        title: "Auto-fix Complete",
        description: `Applied ${fixes?.length || 0} fixes successfully`,
      });
    } catch (error) {
      toast({
        title: "Auto-fix Failed",
        description: error instanceof Error ? error.message : "Failed to apply fixes",
        variant: "destructive",
      });
    }
  };

  if (!canManageContent) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Content quality management requires admin or staff permissions.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isAnalyzing && !report) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Analyzing content quality...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (errors.length > 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span>Analysis Error: {errors[0]}</span>
          </div>
          <Button 
            onClick={analyzeContentQuality} 
            className="mt-4"
            variant="outline"
          >
            Retry Analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Content Quality Dashboard</h2>
          {lastAnalyzed && (
            <p className="text-sm text-muted-foreground">
              Last analyzed: {lastAnalyzed.toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={analyzeContentQuality}
            disabled={isAnalyzing}
            variant="outline"
          >
            {isAnalyzing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            Refresh Analysis
          </Button>
          {report?.criticalIssues?.length > 0 && (
            <Button 
              onClick={handleAutoFix}
              disabled={isAnalyzing}
            >
              <Zap className="h-4 w-4 mr-2" />
              Auto-Fix Issues
            </Button>
          )}
        </div>
      </div>

      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Quality</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.overallScore}%</div>
              <Progress value={report.overallScore} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Critical Issues</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {report.criticalIssues?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Require immediate attention
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Modules Analyzed</CardTitle>
              <Clock className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.totalModules}</div>
              <p className="text-xs text-muted-foreground">
                Across all courses
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {report && (
        <Tabs defaultValue="issues" className="w-full">
          <TabsList>
            <TabsTrigger value="issues">Issues</TabsTrigger>
            <TabsTrigger value="quality">Quality Metrics</TabsTrigger>
            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          </TabsList>

          <TabsContent value="issues" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Critical Issues</CardTitle>
                <CardDescription>
                  Issues that significantly impact learning quality
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.criticalIssues?.length > 0 ? (
                  <div className="space-y-2">
                    {report.criticalIssues.map((issue: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <div className="font-medium">{issue.title}</div>
                          <div className="text-sm text-muted-foreground">{issue.description}</div>
                        </div>
                        <Badge variant="destructive">{issue.severity}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-green-600">No critical issues found!</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Warnings</CardTitle>
                <CardDescription>
                  Issues that could be improved
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.warnings?.length > 0 ? (
                  <div className="space-y-2">
                    {report.warnings.slice(0, 5).map((warning: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <div className="font-medium">{warning.title}</div>
                          <div className="text-sm text-muted-foreground">{warning.description}</div>
                        </div>
                        <Badge variant="secondary">{warning.severity}</Badge>
                      </div>
                    ))}
                    {report.warnings.length > 5 && (
                      <p className="text-sm text-muted-foreground">
                        And {report.warnings.length - 5} more warnings...
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-green-600">No warnings found!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quality" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Quiz Quality</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Modules with Quizzes</span>
                      <span>{report.quizCoverage}%</span>
                    </div>
                    <Progress value={report.quizCoverage} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Content Completeness</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Complete Modules</span>
                      <span>{report.contentCompleteness}%</span>
                    </div>
                    <Progress value={report.contentCompleteness} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Learning Objectives</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Modules with Objectives</span>
                      <span>{report.objectivesCoverage}%</span>
                    </div>
                    <Progress value={report.objectivesCoverage} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Practice Exercises</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Modules with Practice</span>
                      <span>{report.practiceExercisesCoverage}%</span>
                    </div>
                    <Progress value={report.practiceExercisesCoverage} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="recommendations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Improvement Recommendations</CardTitle>
                <CardDescription>
                  Suggested actions to improve content quality
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.recommendations?.length > 0 ? (
                  <div className="space-y-3">
                    {report.recommendations.map((rec: any, index: number) => (
                      <div key={index} className="p-3 border rounded">
                        <div className="font-medium">{rec.title}</div>
                        <div className="text-sm text-muted-foreground mt-1">{rec.description}</div>
                        <div className="flex items-center mt-2 space-x-2">
                          <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'}>
                            {rec.priority} priority
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Est. {rec.estimatedTime} to complete
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-green-600">No recommendations needed - content quality is excellent!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};